import { Search } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Card, Col, Form, InputGroup, Row, Table } from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDebounce } from 'use-debounce'
import Loader from '../../../components/atoms/Loader'
import RowEmpty from '../../../components/atoms/RowEmpty'
import TableAction from '../../../components/atoms/Table/TableAction'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import api from '../../../config/AxiosInstance'
import { convertDateWithTime } from '../../../helpers/constants'
import * as Common from '../../../utils/Common'

const initialMeta = {
  per_page: 10,
  current_page: 1,
  total_row: 0,
  total_page: 0
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const tableHeaderStyle = {
  backgroundColor: '#f8f9fa',
  color: '#333333',
  fontWeight: '700',
  borderBottom: '1px solid #e9ecef',
  textTransform: 'uppercase',
  fontSize: '0.85rem'
}

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const MasterItemPage = () => {
  const navigate = useNavigate()
  const { state: stateLocation } = useLocation()
  const [items, setItems] = useState([])
  const [meta, setMeta] = useState(initialMeta)
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState(stateLocation?.filter || '')
  const [itemGroup, setItemGroup] = useState('')
  const [debouncedQuery] = useDebounce(query, 1000)
  const [debouncedItemGroup] = useDebounce(itemGroup, 1000)
  const { per_page, current_page } = meta
  const prevQuery = useRef({ q: debouncedQuery, itemGroup: debouncedItemGroup })
  const abortControllerRef = useRef(null)

  useEffect(() => {
    const queryChanged =
      prevQuery.current.q !== debouncedQuery ||
      prevQuery.current.itemGroup !== debouncedItemGroup

    if (queryChanged) {
      prevQuery.current = { q: debouncedQuery, itemGroup: debouncedItemGroup }
      if (current_page !== 1) {
        setMeta((prev) => ({ ...prev, current_page: 1 }))
        return
      }
    }

    fetchItems()

    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, debouncedItemGroup, per_page, current_page])

  async function fetchItems() {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    setLoading(true)
    try {
      const params = {
        q: debouncedQuery || '',
        item_group: debouncedItemGroup || '',
        per_page,
        page: current_page
      }
      const response = await api.get('master/items', { params, signal })
      const { data, metadata } = response.data
      setItems(data)
      setMeta(metadata)
    } catch (err) {
      if (
        err.name !== 'CanceledError' &&
        err.message !== 'canceled' &&
        err.code !== 'ERR_CANCELED'
      ) {
        const errMsg =
          err?.response?.data?.errors?.message || 'An error occurred'
        Common.showPopUpErrorMessage(errMsg)
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false)
      }
    }
  }

  const handleOnChangeMeta = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
  }

  const handlePageChange = (data) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      current_page: data.selected + 1
    }))
    window.scrollTo(0, 0)
  }

  const handleDetail = (code) => {
    const encodedName = encodeURIComponent(code)
    navigate(`/master/items/${encodedName}`, {
      state: {
        filter: query
      }
    })
  }

  return (
    <div className="container-fluid px-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h3 style={headerTitleStyle}>Master Item</h3>
        </Col>
      </Row>

      <Card style={cardFilterStyle}>
        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label
                className="text-uppercase fw-bold text-dark mb-2"
                style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}
              >
                Item Name/Code
              </Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#fff',
                    border: '1px solid #e9ecef',
                    borderRight: 'none',
                    borderTopLeftRadius: '8px',
                    borderBottomLeftRadius: '8px'
                  }}
                >
                  <Search size={18} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search Item Name or Code..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  style={{
                    height: '45px',
                    borderTopRightRadius: '8px',
                    borderBottomRightRadius: '8px',
                    border: '1px solid #e9ecef',
                    borderLeft: 'none',
                    fontSize: '0.9rem'
                  }}
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label
                className="text-uppercase fw-bold text-dark mb-2"
                style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}
              >
                Search Item Group
              </Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#fff',
                    border: '1px solid #e9ecef',
                    borderRight: 'none',
                    borderTopLeftRadius: '8px',
                    borderBottomLeftRadius: '8px'
                  }}
                >
                  <Search size={18} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search Item Group..."
                  value={itemGroup}
                  onChange={(e) => setItemGroup(e.target.value)}
                  style={{
                    height: '45px',
                    borderTopRightRadius: '8px',
                    borderBottomRightRadius: '8px',
                    border: '1px solid #e9ecef',
                    borderLeft: 'none',
                    fontSize: '0.9rem'
                  }}
                />
              </InputGroup>
            </Form.Group>
          </Col>
        </Row>
      </Card>

      <Row className="mt-3">
        <Col md={12}>
          <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
            <Table responsive hover className="mb-0 b-table-frameless">
              <thead style={tableHeaderStyle}>
                <tr>
                  <th className="p-2 align-middle text-end">Action</th>
                  <th className="p-2 align-middle">Item Name</th>
                  <th className="p-2 align-middle">Item Code</th>
                  <th className="p-2 align-middle">Item Group</th>
                  <th className="p-2 align-middle">Description</th>
                  <th className="p-2 align-middle">Qty/Unit</th>
                  <th className="p-2 align-middle">UOM</th>
                  <th className="p-2 align-middle">Primary Item Group</th>
                  <th className="p-2 align-middle">P x L x T / cm</th>
                  <th className="p-2 align-middle">Volume / cm³</th>
                  <th className="p-2 align-middle">Last Update</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={12} className="text-center p-5">
                      <Loader />
                    </td>
                  </tr>
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={12}>
                      <RowEmpty colSpan={12} />
                    </td>
                  </tr>
                )}
                {!loading &&
                  items.map((item, i) => (
                    <tr key={i}>
                      <td className="p-2 align-middle text-end">
                        <TableAction>
                          <button
                            className="btn btn-light shadow-sm rounded d-flex align-items-center justify-content-center p-0"
                            style={{ width: '32px', height: '32px' }}
                            onClick={() => handleDetail(item.item_code)}
                          >
                            <Search size={13} />
                          </button>
                        </TableAction>
                      </td>
                      <td className="p-2 align-middle fw-medium text-dark">
                        {item?.item_name}
                      </td>
                      <td className="p-2 align-middle">{item?.item_code}</td>
                      <td className="p-2 align-middle">{item?.item_group}</td>
                      <td className="p-2 align-middle">{item?.description}</td>
                      <td className="p-2 align-middle">{item?.qty_unit}</td>
                      <td className="p-2 align-middle">{item?.uom}</td>
                      <td className="p-2 align-middle">
                        {item?.primary_item_group || '-'}
                      </td>
                      <td className="p-2 align-middle">{item?.p_l_t}</td>
                      <td className="p-2 align-middle">{item?.volume_item}</td>
                      <td className="p-2 align-middle">
                        {convertDateWithTime(item?.updatedAt)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
            {items.length > 0 && (
              <div className="p-3 border-top bg-white">
                <TablePagination
                  meta={meta}
                  handlePageChange={handlePageChange}
                  handleOnChangeMeta={handleOnChangeMeta}
                />
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default MasterItemPage
